import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, switchMap, map, catchError, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { State } from '..';
import { QueuesService } from '../../services/queues.service';
import { errorHappened } from '../profile/profile.actions';
import {
  approveFormSubmission, approveFormSubmissionFailure, approveFormSubmissionSuccess,
  dismissHrEmployeeChange, dismissHrEmployeeChangeFailure, dismissHrEmployeeChangeSuccess,
  awardReferralBonus, awardReferralBonusFail, awardReferralBonusSuccess,
  deleteEmployeeReferral, deleteEmployeeReferralFail, deleteEmployeeReferralSuccess,
  deleteReferralBonus, deleteReferralBonusFail, deleteReferralBonusSuccess,
  getEmployeeReferral, getEmployeeReferralFailure, getEmployeeReferralList,
  getEmployeeReferralListSuccess, getEmployeeReferralSuccess,
  getFormApprovalCount, getFormApprovalCountSuccess,
  getFormApprovalQueue, getFormApprovalQueueSuccess,
  getHrEmployeeChangeCount, getHrEmployeeChangeCountSuccess,
  getHrEmployeeChangeQueue, getHrEmployeeChangeQueueSuccess,
  getMyReferrals, getMyReferralsSuccess,
  getUploadDocApprovalCount, getUploadDocApprovalCountSuccess,
  loadFormApprovalFormList, loadFormApprovalFormListSuccess,
  recalculateReferralBonus, recalculateReferralBonusFail, recalculateReferralBonusSuccess,
  rejectEmployeeReferral, rejectEmployeeReferralFail, rejectEmployeeReferralSuccess,
  resetEmployeeReferral, resetEmployeeReferralFail, resetEmployeeReferralSuccess,
  saveEmployeeReferral, saveEmployeeReferralFailure, saveEmployeeReferralSuccess,
  toggleArchiveEmployeeReferral, toggleArchiveEmployeeReferralFail, toggleArchiveEmployeeReferralSuccess,
  createReferralFormSubmission, createReferralFormSubmissionSuccess, createReferralFormSubmissionFail
} from './queues.action';

@Injectable()
export class QueuesEffects {
  constructor(private actions$: Actions,
    private queuesService: QueuesService,
    private store: Store<State>) { }

  loadFormApprovalFormList = createEffect(() => this.actions$.pipe(
    ofType(loadFormApprovalFormList),
    switchMap(action => this.queuesService.getFormApprovalFormList().pipe(
      map(formList => loadFormApprovalFormListSuccess({ formList })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))));

  getFormApprovalQueue = createEffect(() => this.actions$.pipe(
    ofType(getFormApprovalQueue),
    switchMap(action => {
      return this.queuesService.getFormApprovalQueue(action.filter).pipe(
        map(list => getFormApprovalQueueSuccess({ list })),
        catchError(err => {
          return of(errorHappened({ err }));
        }));
    })
  ));

  approveFormSubmission = createEffect(() => this.actions$.pipe(
    ofType(approveFormSubmission),
    switchMap(action => {
      return this.queuesService.approveFormSubmission(action.formSubmissionId).pipe(
        map(list => approveFormSubmissionSuccess()),
        catchError(err => {
          this.store.dispatch(approveFormSubmissionFailure());
          return of(errorHappened({ err }));
        }));
    })
  ));


  getUploadDocApprovalCount = createEffect(() => this.actions$.pipe(
    ofType(getUploadDocApprovalCount),
    switchMap(action => this.queuesService.getUploadDocApprovalCount().pipe(
      map(count => getUploadDocApprovalCountSuccess({ count })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))));

  getFormApprovalCount = createEffect(() => this.actions$.pipe(
    ofType(getFormApprovalCount),
    switchMap(action => this.queuesService.getFormApprovalCount().pipe(
      map(count => getFormApprovalCountSuccess({ count })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))));

  getHrEmployeeChangeQueue = createEffect(() => this.actions$.pipe(
    ofType(getHrEmployeeChangeQueue),
    switchMap(action => {
      return this.queuesService.getHrEmployeeChangeQueue(action.filter).pipe(
        map(list => getHrEmployeeChangeQueueSuccess({ list })),
        catchError(err => {
          return of(errorHappened({ err }));
        }));
    })
  ));

  getHrEmployeeChangeCount = createEffect(() => this.actions$.pipe(
    ofType(getHrEmployeeChangeCount),
    switchMap(action => this.queuesService.getHrEmployeeChangeCount().pipe(
      map(count => getHrEmployeeChangeCountSuccess({ count })),
      catchError(err => {
        return of(errorHappened({ err }));
      })
    ))));

  dismissHrEmployeeChange = createEffect(() => this.actions$.pipe(
    ofType(dismissHrEmployeeChange),
    switchMap(action => {
      return this.queuesService.dismissHrEmployeeChange(action.notificationId).pipe(
        map(list => dismissHrEmployeeChangeSuccess()),
        catchError(err => {
          this.store.dispatch(dismissHrEmployeeChangeFailure());
          return of(errorHappened({ err }));
        }));
    })
  ));

  getEmployeeReferral = createEffect(() => this.actions$.pipe(
    ofType(getEmployeeReferral),
    switchMap(action => this.queuesService.getEmployeeReferral(action.referralId).pipe(
      map(referral => getEmployeeReferralSuccess({ referral })),
      catchError(err => {
        this.store.dispatch(getEmployeeReferralFailure({ err }));
        return of(errorHappened({ err }));
      })
    ))));

  saveEmployeeReferral = createEffect(() => this.actions$.pipe(
    ofType(saveEmployeeReferral),
    switchMap(action => this.queuesService.saveEmployeeReferral(action.referral).pipe(
      map(referral => saveEmployeeReferralSuccess()),
      catchError(err => {
        this.store.dispatch(saveEmployeeReferralFailure({ err }));
        return of(errorHappened({ err }));
      })
    ))));

  getEmployeeReferralList = createEffect(() => this.actions$.pipe(
    ofType(getEmployeeReferralList),
    switchMap(action => {
      return this.queuesService.getEmployeeReferralList(action.filter).pipe(
        map(referralList => getEmployeeReferralListSuccess({ referralList })),
        catchError(err => {
          return of(errorHappened({ err }));
        }));
    })
  ));

  createReferralFormSubmission = createEffect(() => this.actions$.pipe(
    ofType(createReferralFormSubmission),
    switchMap(action => {
      return this.queuesService.createReferralFormSubmission(action.referralId).pipe(
        map(referral => createReferralFormSubmissionSuccess()),
        catchError(err => {
          this.store.dispatch(createReferralFormSubmissionFail(err));
          return of(errorHappened({ err }));
        }));
    })
  ));

  awardReferralBonus = createEffect(() => this.actions$.pipe(
    ofType(awardReferralBonus),
    switchMap(action => {
      return this.queuesService.awardReferralBonus(action.referralId).pipe(
        map(referral => awardReferralBonusSuccess()),
        catchError(err => {
          this.store.dispatch(awardReferralBonusFail(err));
          return of(errorHappened({ err }));
        }));
    })
  ));

  recalculateReferralBonus = createEffect(() => this.actions$.pipe(
    ofType(recalculateReferralBonus),
    switchMap(action => {
      return this.queuesService.recalculateReferralBonus(action.payrollBonusId).pipe(
        map(referral => recalculateReferralBonusSuccess()),
        catchError(err => {
          this.store.dispatch(recalculateReferralBonusFail(err));
          return of(errorHappened({ err }));
        }));
    })
  ));

  deleteReferralBonus = createEffect(() => this.actions$.pipe(
    ofType(deleteReferralBonus),
    switchMap(action => {
      return this.queuesService.deleteReferralBonus(action.payrollBonusId).pipe(
        map(referral => deleteReferralBonusSuccess()),
        catchError(err => {
          this.store.dispatch(deleteReferralBonusFail(err));
          return of(errorHappened({ err }));
        }));
    })
  ));

  toggleArchiveEmployeeReferral = createEffect(() => this.actions$.pipe(
    ofType(toggleArchiveEmployeeReferral),
    switchMap(action => {
      return this.queuesService.toggleArchiveEmployeeReferral(action.referralId, action.reason).pipe(
        map(referral => toggleArchiveEmployeeReferralSuccess()),
        catchError(err => {
          this.store.dispatch(toggleArchiveEmployeeReferralFail(err));
          return of(errorHappened({ err }));
        }));
    })
  ));

  deleteEmployeeReferral = createEffect(() => this.actions$.pipe(
    ofType(deleteEmployeeReferral),
    switchMap(action => {
      return this.queuesService.deleteEmployeeReferral(action.referralId).pipe(
        map(referral => deleteEmployeeReferralSuccess()),
        catchError(err => {
          this.store.dispatch(deleteEmployeeReferralFail(err));
          return of(errorHappened({ err }));
        }));
    })
  ));

  getMyEmployeeReferralList = createEffect(() => this.actions$.pipe(
    ofType(getMyReferrals),
    switchMap(action => {
      return this.queuesService.getMyEmployeeReferralList(action.filter).pipe(
        map(referralList => getMyReferralsSuccess({ referralList })),
        catchError(err => {
          return of(errorHappened({ err }));
        }));
    })
  ));

  rejectEmployeeReferral = createEffect(() => this.actions$.pipe(
    ofType(rejectEmployeeReferral),
    switchMap(action => {
      return this.queuesService.rejectEmployeeReferral(action.referralId, action.reason).pipe(
        map(referral => rejectEmployeeReferralSuccess()),
        catchError(err => {
          this.store.dispatch(rejectEmployeeReferralFail(err));
          return of(errorHappened({ err }));
        }));
    })
  ));

  resetEmployeeReferral = createEffect(() => this.actions$.pipe(
    ofType(resetEmployeeReferral),
    switchMap(action => {
      return this.queuesService.resetEmployeeReferral(action.referralId).pipe(
        map(referral => resetEmployeeReferralSuccess()),
        catchError(err => {
          this.store.dispatch(resetEmployeeReferralFail(err));
          return of(errorHappened({ err }));
        }));
    })
  ));
}
